// Also the `state` sent in the FACE_MATCH override snapshot. The BE stores that snapshot as-is (no enum on its
// side), so these names are the audit vocabulary; NOT_COMPLETED and BELOW_THRESHOLD match the BE's own tests.
export const FACE_MATCH_STATE = {
    SATISFIED: 'SATISFIED',
    NOT_COMPLETED: 'NOT_COMPLETED',
    IN_PROGRESS: 'IN_PROGRESS',
    BELOW_THRESHOLD: 'BELOW_THRESHOLD',
    FAILED: 'FAILED',
};

export const FACE_MATCH_ATTEMPT_STATUS = {
    PENDING: 'PENDING',
    SUCCESS: 'SUCCESS',
    ERROR: 'ERROR',
};

export const FACE_MATCH_FAILURE = {
    NO_FACE_DETECTED: 'NO_FACE_DETECTED',
    IMAGE_TOO_LARGE: 'IMAGE_TOO_LARGE',
    INVALID_IMAGE_FORMAT: 'INVALID_IMAGE_FORMAT',
    SNAPSHOT_ERROR: 'SNAPSHOT_ERROR',
    TIMEOUT: 'TIMEOUT',
    SERVICE_ERROR: 'SERVICE_ERROR',
};

const KNOWN_FAILURES = new Set(Object.values(FACE_MATCH_FAILURE));

export const normalizeFaceMatchFailure = (code) => (KNOWN_FAILURES.has(code) ? code : FACE_MATCH_FAILURE.SERVICE_ERROR);

// Rounded the way the face match history shows it, so the agent never sees a passing score get blocked.
export const toFaceMatchScore = (similarity) => (typeof similarity === 'number' && Number.isFinite(similarity) ? Math.round(similarity) : null);

export const meetsFaceMatchThreshold = (similarity, threshold) => {
    const score = toFaceMatchScore(similarity);
    return score !== null && score >= threshold;
};

export const resolveFaceMatchConfig = (gate) => ({
    enforced: !!gate?.faceMatchEnforcement,
    threshold: Number.isFinite(gate?.faceMatchThreshold) ? gate.faceMatchThreshold : 0,
});

// Only the latest attempt on the transaction counts: a retry replaces the earlier result, good or bad.
export function resolveFaceMatchStatus({ enforced, threshold }, latestAttempt) {
    const status = (state, details = {}) => ({ state, score: null, threshold, failure: null, ...details });

    if (!enforced) return status(FACE_MATCH_STATE.SATISFIED);
    if (!latestAttempt) return status(FACE_MATCH_STATE.NOT_COMPLETED);
    if (latestAttempt.status === FACE_MATCH_ATTEMPT_STATUS.PENDING) return status(FACE_MATCH_STATE.IN_PROGRESS);
    if (latestAttempt.status === FACE_MATCH_ATTEMPT_STATUS.ERROR) {
        return status(FACE_MATCH_STATE.FAILED, { failure: normalizeFaceMatchFailure(latestAttempt.errorCode) });
    }

    const score = toFaceMatchScore(latestAttempt.similarity);
    // No similarity means Rekognition found no face to compare: a verification failure, never a low score.
    if (score === null) return status(FACE_MATCH_STATE.FAILED, { failure: FACE_MATCH_FAILURE.NO_FACE_DETECTED });

    return status(score >= threshold ? FACE_MATCH_STATE.SATISFIED : FACE_MATCH_STATE.BELOW_THRESHOLD, { score });
}

export const blocksSubmitOnFaceMatch = (faceMatchStatus) => !!faceMatchStatus && faceMatchStatus.state !== FACE_MATCH_STATE.SATISFIED;

// How the two images of a face match were obtained, and from which camera pair. Read by the face match activity
// events (BEAST-13916), which map them to their own wire values.
export const FACE_MATCH_IMAGE_SOURCE = {
    LIVE_STREAMS: 'LIVE_STREAMS',
    SELECTED_SNAPSHOTS: 'SELECTED_SNAPSHOTS',
};

export const FACE_MATCH_CAMERA_PAIR = {
    TRUCK: 'TRUCK',
    CAR: 'CAR',
    MIXED: 'MIXED',
};

// Both images from the same pair name that pair; a plain camera snapshot, or images from two pairs, are mixed.
export const resolveSelectedCameraPair = (faceImage, idImage) =>
    faceImage?.matchType && faceImage.matchType === idImage?.matchType ? faceImage.matchType : FACE_MATCH_CAMERA_PAIR.MIXED;
